export const NOTIFICATION_PREFERENCE_KEYS = [
  'directMessages',
  'postReplies',
  'watchedChannelPosts',
  'watchedChatRoomComments',
  'postLikes',
] as const;

export type NotificationPreferenceKey = (typeof NOTIFICATION_PREFERENCE_KEYS)[number];

export type NotificationPreferenceMap = Record<NotificationPreferenceKey, boolean>;

export type NotificationPreferenceDelivery = 'push' | 'email' | 'inApp';

export type NotificationPreferenceSet = {
  push: NotificationPreferenceMap;
  email: NotificationPreferenceMap;
  inApp: NotificationPreferenceMap;
};

export const NOTIFICATION_PREFERENCE_META: Array<{
  key: NotificationPreferenceKey;
  label: string;
  description: string;
}> = [
  {
    key: 'directMessages',
    label: 'Direct messages',
    description: 'One-to-one messages from other Adulting members.',
  },
  {
    key: 'postReplies',
    label: 'Replies to your posts',
    description: 'New replies on posts you started.',
  },
  {
    key: 'watchedChannelPosts',
    label: 'Watched channel posts',
    description: 'New posts in channels you choose to watch.',
  },
  {
    key: 'watchedChatRoomComments',
    label: 'Watched chat room comments',
    description: 'New comments in chat rooms you choose to watch.',
  },
  {
    key: 'postLikes',
    label: 'Likes on your posts',
    description: 'Reactions and likes on your posts.',
  },
];

export const DEFAULT_PUSH_NOTIFICATION_PREFERENCES: NotificationPreferenceMap = {
  directMessages: true,
  postReplies: true,
  watchedChannelPosts: false,
  watchedChatRoomComments: false,
  postLikes: false,
};

export const DEFAULT_EMAIL_NOTIFICATION_PREFERENCES: NotificationPreferenceMap = {
  directMessages: false,
  postReplies: false,
  watchedChannelPosts: false,
  watchedChatRoomComments: false,
  postLikes: false,
};

export const DEFAULT_IN_APP_NOTIFICATION_PREFERENCES: NotificationPreferenceMap = {
  directMessages: true,
  postReplies: true,
  watchedChannelPosts: true,
  watchedChatRoomComments: true,
  postLikes: true,
};

export function cloneNotificationPreferenceMap(source: NotificationPreferenceMap): NotificationPreferenceMap {
  return { ...source };
}

export function normalizeNotificationPreferenceMap(
  value: unknown,
  fallback: NotificationPreferenceMap,
): NotificationPreferenceMap {
  const source = value && typeof value === 'object' ? (value as Partial<Record<NotificationPreferenceKey, unknown>>) : {};

  return NOTIFICATION_PREFERENCE_KEYS.reduce<NotificationPreferenceMap>((acc, key) => {
    acc[key] = typeof source[key] === 'boolean' ? Boolean(source[key]) : fallback[key];
    return acc;
  }, cloneNotificationPreferenceMap(fallback));
}

export function normalizeNotificationPreferenceSet(input: {
  push?: unknown;
  email?: unknown;
  inApp?: unknown;
} | null | undefined): NotificationPreferenceSet {
  return {
    push: normalizeNotificationPreferenceMap(input?.push, DEFAULT_PUSH_NOTIFICATION_PREFERENCES),
    email: normalizeNotificationPreferenceMap(input?.email, DEFAULT_EMAIL_NOTIFICATION_PREFERENCES),
    inApp: normalizeNotificationPreferenceMap(input?.inApp, DEFAULT_IN_APP_NOTIFICATION_PREFERENCES),
  };
}

export function toggleNotificationPreference(
  map: NotificationPreferenceMap,
  key: NotificationPreferenceKey,
): NotificationPreferenceMap {
  return {
    ...map,
    [key]: !map[key],
  };
}
